import { useReactFlow, useStore, type ReactFlowInstance } from '@xyflow/react';
import { useEffect, useRef } from 'react';

interface ChartCaptureGateProps {
  onReady: (rf: ReactFlowInstance) => void;
}

// Fires once nodes have landed in the store and the DOM viewport has settled.
// Must render inside <ReactFlowProvider>.
const ChartCaptureGate = ({ onReady }: ChartCaptureGateProps) => {
  const rf = useReactFlow();
  const nodeCount = useStore((s) => s.nodeLookup.size);
  const calledRef = useRef(false);
  useEffect(() => {
    if (calledRef.current || nodeCount === 0) return undefined;
    // Two RAFs: first commits node DOM, second lets the initial fit
    // tick through. The capture doesn't depend on the viewport transform
    // (we re-translate via PADDING) but waiting avoids racing a half-laid-out
    // tree on dense graphs.
    const f1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (calledRef.current) return;
        calledRef.current = true;
        onReady(rf);
      });
    });
    return () => cancelAnimationFrame(f1);
  }, [onReady, nodeCount, rf]);
  return null;
};

export { ChartCaptureGate };
